import { useState, type ComponentProps } from "react"
import { Trash2, TriangleAlert } from "lucide-react"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { useReadOnly } from "@/store/session"

const READ_ONLY_TITLE = "Суб'єкт виключений з реєстру — доступний лише перегляд"

/**
 * design-system.md's `DeleteDraftDialog` — confirmation before deleting an
 * OTK draft from list/table actions. Built on `AlertDialog`, not `Dialog`,
 * since the action is irreversible; names the document in its description.
 */
export function DeleteDraftDialog({
  documentNumber,
  onConfirm,
  triggerLabel = "Видалити",
  triggerSize,
  triggerVariant = "destructive",
}: {
  documentNumber: string
  onConfirm: () => void | Promise<void>
  triggerLabel?: string
  triggerSize?: ComponentProps<typeof Button>["size"]
  triggerVariant?: ComponentProps<typeof Button>["variant"]
}) {
  const readOnly = useReadOnly()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm()
      setOpen(false)
    } catch {
      setError("Не вдалося видалити чернетку. Спробуйте ще раз.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AlertDialog
      onOpenChange={(nextOpen) => {
        if (nextOpen) setError(null)
        setOpen(nextOpen)
      }}
      open={open}
    >
      <AlertDialogTrigger asChild>
        <Button
          disabled={readOnly}
          size={triggerSize}
          title={readOnly ? READ_ONLY_TITLE : undefined}
          variant={triggerVariant}
        >
          <Trash2 data-icon="inline-start" />
          <span className="max-lg:sr-only">{triggerLabel}</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogMedia className="bg-destructive/10 text-destructive">
            <TriangleAlert />
          </AlertDialogMedia>
          <AlertDialogTitle>Видалити чернетку</AlertDialogTitle>
          <AlertDialogDescription>
            Чернетку {documentNumber} буде видалено назавжди.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Назад</AlertDialogCancel>
          <Button disabled={submitting} onClick={handleConfirm} variant="destructive">
            <Trash2 data-icon="inline-start" />
            {submitting ? "Видалення…" : "Видалити"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
